import type { EngineSignals } from '@fraudeai/schema';
import { defineModule, type ModuleContext } from '../harness.js';
import { attempt, round } from '../util/safe.js';
import { hashValue } from '../util/hash.js';
import { expectedJsEngine } from '../util/ua.js';

/**
 * Отпечаток движка JavaScript и проверка возраста API.
 *
 * UA-строку меняют одной настройкой, а поведение движка — нет. Здесь мы
 * измеряем движок напрямую: тексты ошибок, форму стек-трейсов, младшие биты
 * тригонометрии, набор глобалов. Затем сверяем с тем, что заявлено в UA.
 *
 * Отдельно — проверка возраста: каждая функция языка появилась в конкретной
 * версии браузера. По набору присутствующих и отсутствующих API вычисляется
 * диапазон версий, в который окружение может попадать. Если заявленная версия
 * в него не входит — UA подделан. Если диапазон пуст, набор API не
 * соответствует ни одной реальной сборке.
 */

/** Версия Chrome, в которой возможность стала доступна по умолчанию. */
const CHROME_FEATURE_AGES: [string, number, () => boolean][] = [
  ['String.prototype.replaceAll', 85, () => typeof ''.replaceAll === 'function'],
  ['navigator.userAgentData', 90, () => 'userAgentData' in navigator],
  ['RegExp d flag', 90, () => attempt(() => new RegExp('a', 'd') !== null, false) ?? false],
  ['Array.prototype.at', 92, () => typeof [].at === 'function'],
  ['Object.hasOwn', 93, () => typeof Object.hasOwn === 'function'],
  ['scheduler.postTask', 94, () => 'scheduler' in globalThis],
  ['Array.prototype.findLast', 97, () => typeof [].findLast === 'function'],
  ['structuredClone', 98, () => typeof structuredClone === 'function'],
  ['AbortSignal.timeout', 103, () => typeof AbortSignal?.timeout === 'function'],
  ['navigator.gpu', 113, () => 'gpu' in navigator],
  ['Object.groupBy', 117, () => typeof (Object as { groupBy?: unknown }).groupBy === 'function'],
  [
    'Promise.withResolvers',
    119,
    () => typeof (Promise as { withResolvers?: unknown }).withResolvers === 'function',
  ],
  ['URL.canParse', 120, () => typeof (URL as { canParse?: unknown }).canParse === 'function'],
  ['Array.fromAsync', 121, () => typeof (Array as { fromAsync?: unknown }).fromAsync === 'function'],
  [
    'Set.prototype.union',
    122,
    () => typeof (Set.prototype as { union?: unknown }).union === 'function',
  ],
  [
    'Iterator helpers',
    122,
    () => typeof (globalThis as { Iterator?: { prototype?: { take?: unknown } } }).Iterator
      ?.prototype?.take === 'function',
  ],
  [
    'Promise.try',
    134,
    () => typeof (Promise as { try?: unknown }).try === 'function',
  ],
  [
    'Error.isError',
    134,
    () => typeof (Error as { isError?: unknown }).isError === 'function',
  ],
  [
    'Float16Array',
    135,
    () => typeof (globalThis as { Float16Array?: unknown }).Float16Array === 'function',
  ],
  [
    'RegExp.escape',
    136,
    () => typeof (RegExp as { escape?: unknown }).escape === 'function',
  ],
];

/** Глобалы, характерные для конкретного движка. */
const ENGINE_GLOBALS: [string, string][] = [
  ['InternalError', 'spidermonkey'],
  ['mozInnerScreenX', 'spidermonkey'],
  ['dump', 'spidermonkey'],
  ['chrome', 'v8'],
  ['webkitRequestFileSystem', 'v8'],
  ['showOpenFilePicker', 'v8'],
  ['webkitConvertPointFromNodeToPage', 'javascriptcore'],
  ['GestureEvent', 'javascriptcore'],
  ['webkitAudioContext', 'javascriptcore'],
];

/**
 * Определение движка JavaScript по поведению.
 *
 * Опираемся на набор собственных свойств объекта ошибки. Он у каждого движка
 * свой и, в отличие от отдельных функций, между движками не переезжает:
 * SpiderMonkey заполняет `lineNumber`/`columnNumber`/`fileName`,
 * JavaScriptCore — `line`/`column`/`sourceURL`, V8 не заполняет ничего из
 * этого и вместо того печатает кадры стека строками «    at ».
 *
 * Историческая ошибка, которую здесь чинили: раньше первой проверкой шло
 * наличие `Error.captureStackTrace` с комментарием «это расширение V8,
 * больше нигде его нет». Так было до Safari 16.4 — потом JavaScriptCore эту
 * функцию тоже реализовал, и весь Safari стал определяться как Chrome.
 * Отсюда правило: разделять движки по признакам, которые они не копируют
 * друг у друга, а не по наличию отдельной функции.
 */
function detectEngine(): string {
  const probe = attempt(() => new Error('probe'));

  if (probe) {
    if (attempt(() => 'lineNumber' in probe && 'columnNumber' in probe, false)) {
      return 'spidermonkey';
    }
    if (attempt(() => 'sourceURL' in probe || ('line' in probe && 'column' in probe), false)) {
      return 'javascriptcore';
    }
  }

  // InternalError — тип ошибки, существующий только в SpiderMonkey.
  if (attempt(() => typeof (globalThis as { InternalError?: unknown }).InternalError === 'function', false)) {
    return 'spidermonkey';
  }

  const stack = attempt(() => {
    try {
      throw new Error('probe');
    } catch (error) {
      return String((error as Error).stack ?? '');
    }
  });

  if (stack) {
    if (/\n\s+at\s/.test(stack)) return 'v8';
    if (/^\s*[^\s@]*@\S+:\d+:\d+/m.test(stack)) return 'javascriptcore';
  }

  return 'unknown';
}

function collectErrorSamples(): Record<string, string> {
  const samples: Record<string, string> = {};
  const cases: [string, () => unknown][] = [
    ['null-prop', () => (null as unknown as Record<string, unknown>)['x']],
    ['not-a-function', () => (undefined as unknown as () => void)()],
    ['array-length', () => new Array(-1)],
    ['json', () => JSON.parse('{')],
    ['tofixed', () => (1).toFixed(101)],
    ['uri', () => decodeURIComponent('%')],
    ['frozen', () => Object.defineProperty(Object.freeze({}), 'a', { value: 1 })],
    ['bad-timezone', () => new Intl.DateTimeFormat('en', { timeZone: 'Not/AZone' })],
    ['proto-cycle', () => {
      const a: Record<string, unknown> = {};
      Object.setPrototypeOf(a, Object.create(a));
    }],
    ['bigint', () => (1n as unknown as number) + 1],
  ];

  // Здесь нужен именно текст исключения, поэтому ловим напрямую,
  // а не через attempt — тот сообщение проглатывает.
  for (const [name, fn] of cases) {
    try {
      fn();
      samples[name] = 'no-throw';
    } catch (error) {
      samples[name] = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    }
  }
  return samples;
}

function collectMathSamples(): Record<string, number> {
  const out: Record<string, number> = {};
  const cases: [string, () => number][] = [
    ['acos', () => Math.acos(0.123456789)],
    ['acosh', () => Math.acosh(1e300)],
    ['asin', () => Math.asin(0.123456789)],
    ['asinh', () => Math.asinh(1)],
    ['atan', () => Math.atan(2)],
    ['atanh', () => Math.atanh(0.5)],
    ['cbrt', () => Math.cbrt(100)],
    ['cos1e10', () => Math.cos(1e10)],
    ['cosh', () => Math.cosh(1)],
    ['exp', () => Math.exp(1)],
    ['expm1', () => Math.expm1(1)],
    ['log', () => Math.log(10)],
    ['log1p', () => Math.log1p(10)],
    ['sin1e10', () => Math.sin(1e10)],
    ['sinh', () => Math.sinh(1)],
    ['tan', () => Math.tan(-1e300)],
    ['tanh', () => Math.tanh(1)],
    ['powPi', () => Math.pow(Math.PI, -100)],
    ['hypot', () => Math.hypot(3, 4, 5)],
  ];
  for (const [name, fn] of cases) {
    const value = attempt(fn);
    if (value !== null) out[name] = value;
  }
  return out;
}

function measureTimerResolution(): number | null {
  return attempt(() => {
    let min = Number.POSITIVE_INFINITY;
    let last = performance.now();
    for (let i = 0; i < 20000; i++) {
      const t = performance.now();
      const delta = t - last;
      if (delta > 0 && delta < min) min = delta;
      last = t;
      // Ниже наносекунды измерять бессмысленно — выходим раньше.
      if (min <= 0.000001) break;
    }
    return Number.isFinite(min) ? round(min, 9) : null;
  });
}

export const engineModule = defineModule({
  id: 'engine',
  phase: 1,
  timeoutMs: 5000,
  run(ctx: ModuleContext): EngineSignals {
    const parsed = ctx.signals.navigator?.parsed ?? null;
    const guessFromUa = parsed ? expectedJsEngine(parsed.browser, parsed.platformFamily) : null;

    const errorSamples = collectErrorSamples();
    const mathSamples = collectMathSamples();
    const timerResolution = measureTimerResolution();

    const stackShape = attempt(() => {
      try {
        throw new Error('probe');
      } catch (error) {
        const stack = String((error as Error).stack ?? '');
        if (stack.includes('    at ')) return 'v8-at';
        if (/@\S+:\d+:\d+/.test(stack)) return 'at-sign';
        return stack.slice(0, 32);
      }
    });

    const engineGlobals = attempt(() =>
      ENGINE_GLOBALS.filter(([name]) => name in globalThis).map(([name, engine]) => `${name}:${engine}`),
    );

    // Возраст API имеет смысл только для Chromium: у Firefox и Safari своя
    // нумерация, и таблица к ним неприменима.
    let featureAgeMin: number | null = null;
    let featureAgeMax: number | null = null;
    const missingExpectedFeatures: string[] = [];
    const unexpectedFeatures: string[] = [];

    // Сверяем именно с версией Chromium: у производных браузеров своя
    // нумерация, и по ней возраст возможностей языка не определить.
    if (guessFromUa === 'v8' && parsed?.chromiumMajor) {
      const claimed = parsed.chromiumMajor;
      let minRequired = 0;
      let maxAllowed = Number.POSITIVE_INFINITY;

      for (const [name, version, test] of CHROME_FEATURE_AGES) {
        const present = attempt(test, false) ?? false;
        if (present) {
          // Раз возможность есть, сборка не может быть старее версии её выхода.
          minRequired = Math.max(minRequired, version);
          if (claimed < version) unexpectedFeatures.push(`${name} (Chrome ${version}+)`);
        } else {
          // Раз возможности нет, сборка не может быть новее последней версии без неё.
          maxAllowed = Math.min(maxAllowed, version - 1);
          if (claimed >= version) missingExpectedFeatures.push(`${name} (Chrome ${version}+)`);
        }
      }

      featureAgeMin = minRequired || null;
      featureAgeMax = Number.isFinite(maxAllowed) ? maxAllowed : null;
    }

    return {
      guess: detectEngine(),
      guessFromUa,
      mathHash: hashValue(mathSamples),
      mathSamples,
      errorSignature: hashValue(errorSamples),
      errorSamples,
      stackShape,
      intlHash: attempt(() =>
        hashValue({
          resolved: new Intl.DateTimeFormat('en-US').resolvedOptions(),
          plural: new Intl.PluralRules('ru-RU').select(2),
          list: new Intl.ListFormat('en', { type: 'disjunction' }).format(['a', 'b', 'c']),
          segments: attempt(() =>
            Array.from(
              new Intl.Segmenter('ja', { granularity: 'word' }).segment('今日はいい天気ですね'),
            ).length,
          ),
          display: new Intl.DisplayNames(['en'], { type: 'region' }).of('RU'),
        }),
      ),
      regexpBehaviour: attempt(() => ({
        namedGroups: String(/(?<y>a)/.exec('a')?.groups?.['y']),
        unicodeProperty: String(/\p{Script=Cyrillic}/u.test('ж')),
        flagsOrder: new RegExp('a', 'gimsuy').flags,
        lookbehind: attempt(() => String(/(?<=a)b/.test('ab')), 'unsupported') as string,
        vFlag: attempt(() => String(new RegExp('[\\q{ab}]', 'v').test('ab')), 'unsupported') as string,
      })),
      numberFormatting: attempt(() => ({
        e21: (1e21).toString(),
        floatSum: (0.1 + 0.2).toString(),
        negZero: String(1 / -0),
        bigInt: (1000000000000000128).toString(),
        precision: (0.000001).toString(),
        maxSafe: Number.MAX_SAFE_INTEGER.toString(),
        exp: (123.456).toExponential(),
      })),
      timerResolution,
      // Грубое округление таймера — защита от timing-атак, включённая
      // при кросс-доменной изоляции или в приватных сборках.
      performanceNowPrecisionBits:
        timerResolution && timerResolution > 0
          ? Math.round(-Math.log2(timerResolution))
          : null,
      engineGlobals,
      featureAgeMin,
      featureAgeMax,
      missingExpectedFeatures: missingExpectedFeatures.length ? missingExpectedFeatures : null,
      unexpectedFeatures: unexpectedFeatures.length ? unexpectedFeatures : null,
    };
  },
});
